import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ActivityAppDispatch, useActivitySelector } from '../../Redux/activityStore';
import { retryWorkflow } from '../../Redux/activitySlice';
import { selectRetryingWorkflowIds } from '../../Redux/activitySelectors';
import ActivityStatusBadge from '../shared/ActivityStatusBadge';
import { TableCell, TableRow, SkeletonLoader, Tooltip } from '@forge/common';
import '../activity.scss';
import { IActivityItem } from '../../Redux/activityModels';
import { formatDate, formatModifiedDataText, translateWorkflowTaskDescription } from '../../utils';
import { makeSelectNameCellState, NameCellState } from '../../activityNameCell';
import ActivityRetryButton from '../shared/ActivityRetryButton';

export interface ActivityTableRowProps {
  item: IActivityItem;
  visibleColumns: string[];
  onRowClick: (item: IActivityItem) => void;
}

const ActivityTableRow: React.FC<ActivityTableRowProps> = ({ item, visibleColumns, onRowClick }) => {
  const { updatedAt, createdAt, description, customerName, siteName, status, createdBy, updatedBy, details, correlationId } = item;
  const dispatch = useDispatch<ActivityAppDispatch>();
  const retryingWorkflowIds = useActivitySelector(selectRetryingWorkflowIds);
  const { t } = useTranslation();

  const selectCreatedByState = useMemo(() => makeSelectNameCellState(createdBy), [createdBy]);
  const selectUpdatedByState = useMemo(() => makeSelectNameCellState(updatedBy), [updatedBy]);
  const createdByState = useActivitySelector(selectCreatedByState);
  const updatedByState = useActivitySelector(selectUpdatedByState);

  const renderNameCell = (state: NameCellState): React.ReactNode => {
    switch (state.kind) {
      case 'loading':  return <SkeletonLoader className="tw-w-16" />;
      case 'dash':     return null;
      case 'resolved': return <>{state.name}</>;
    }
  };

  const handleRowClick = useCallback(() => {
    onRowClick(item);
  }, [item, onRowClick]);

  const handleRetryClick = useCallback(() => {
    const failedTaskIds = item.tasks
      .filter(t => t.retry)
      .map(t => t.id);
    dispatch(retryWorkflow({ workflowId: item.id, taskIds: failedTaskIds }));
  }, [item.id, item.tasks, dispatch]);

  const hasRetryableTasks = item.tasks.some(t => t.retry);
  const isRetryingWorkflow = retryingWorkflowIds.includes(item.id);

  const detailsCell = useMemo<React.ReactNode>(() => {
    const text = formatModifiedDataText(details, t);
    if (!text) return null;
    return (
      <Tooltip content={text} position="left-bottom">
        <div className="tw-truncate tw-max-w-xs tw-text-sm tw-cursor-default">
          {text}
        </div>
      </Tooltip>
    );
  }, [details, t]);

  const cellMap = useMemo<Record<string, React.ReactNode>>(() => ({
    updatedAt: formatDate(updatedAt) || null,
    createdAt: formatDate(createdAt) || null,
    description: description ? <span className="activity-link-text">{translateWorkflowTaskDescription(description, t)}</span> : null,
    customerName: customerName ? <span className="activity-link-text">{customerName}</span> : null,
    siteName: siteName ? <span className="activity-link-text">{siteName}</span> : null,
    status: (
      <div className="tw-inline-flex tw-items-center tw-gap-1">
        <ActivityStatusBadge status={status} showFailedIcon />
        {hasRetryableTasks && (
          <ActivityRetryButton
            className="activity-retry-btn tw-flex-shrink-0 tw-justify-start"
            onClick={handleRetryClick}
            isRetrying={isRetryingWorkflow}
          />
        )}
      </div>
    ),
    createdBy: renderNameCell(createdByState),
    updatedBy: renderNameCell(updatedByState),
    details: detailsCell,
  }), [updatedAt, createdAt, description, customerName, siteName, status, hasRetryableTasks,
    isRetryingWorkflow, handleRetryClick, createdByState, updatedByState, t, detailsCell]);

  return (
    <TableRow onClick={handleRowClick} className="tw-cursor-pointer">
      {visibleColumns.map(colId => (
        <TableCell key={colId} variant="compact">{cellMap[colId]}</TableCell>
      ))}
    </TableRow>
  );
};

export default ActivityTableRow;
